import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, Clock, Award, Download, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { renderRichContent } from '@/lib/sanitizeHtml';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const QUIZ_PASS = 70; // %

export default function FormationUseCase() {
    const { slug } = useParams();
    const { user, session } = useAuth();
    const { lang, t } = useLang();
    const [useCase, setUseCase] = useState(null);
    const [progress, setProgress] = useState({ completed_modules: [], progress: 0, metadata: {} });
    const [activeIdx, setActiveIdx] = useState(0);
    const [answers, setAnswers] = useState({}); // { [moduleKey]: { [qIdx]: optionIdx } }
    const [submittedKey, setSubmittedKey] = useState(null); // module currently showing results
    const [downloadingCert, setDownloadingCert] = useState(false);

    useEffect(() => {
        (async () => {
            const { data: uc } = await supabase.from('training_use_cases').select('*').eq('slug', slug).maybeSingle();
            setUseCase(uc);
            if (uc && user) {
                const { data: p } = await supabase
                    .from('training_progress')
                    .select('*')
                    .eq('user_id', user.id).eq('use_case_id', uc.id).maybeSingle();
                if (p) setProgress({ completed_modules: [], progress: 0, metadata: {}, ...p });
            }
        })();
    }, [slug, user]);

    const modules = useCase?.modules || [];
    const active = modules[activeIdx];
    const done = useMemo(() => new Set(progress.completed_modules || []), [progress.completed_modules]);
    const quizScores = (progress.metadata && progress.metadata.quiz_scores) || {};
    const allDone = modules.length > 0 && modules.every(m => done.has(m.key));

    const completeModule = async (key, scorePct) => {
        if (!useCase || !user) return;
        const completed = Array.from(new Set([...(progress.completed_modules || []), key]));
        const pct = Math.round((completed.length / modules.length) * 100);
        const newScores = { ...quizScores, [key]: scorePct };
        const next = {
            user_id: user.id,
            use_case_id: useCase.id,
            completed_modules: completed,
            progress: pct,
            metadata: { ...(progress.metadata || {}), quiz_scores: newScores },
        };
        const { data, error } = await supabase
            .from('training_progress')
            .upsert(next, { onConflict: 'user_id,use_case_id' })
            .select().single();
        if (error) { toast.error(`Sauvegarde progression : ${error.message}`); return; }
        setProgress(data || next);
        toast.success(`Module validé · Score ${scorePct} %`);
        const idx = modules.findIndex(m => m.key === key);
        if (idx >= 0 && idx + 1 < modules.length) setActiveIdx(idx + 1);
    };

    const submitQuiz = async (m) => {
        const quiz = m.quiz || [];
        if (quiz.length === 0) {
            // No quiz defined for this module → mark complete with 100%
            await completeModule(m.key, 100);
            return;
        }
        const moduleAnswers = answers[m.key] || {};
        if (Object.keys(moduleAnswers).length !== quiz.length) {
            toast.error('Veuillez répondre à toutes les questions');
            return;
        }
        let correct = 0;
        quiz.forEach((q, i) => { if (moduleAnswers[i] === q.correct) correct++; });
        const score = Math.round((correct / quiz.length) * 100);
        setSubmittedKey(m.key);
        if (score < QUIZ_PASS) {
            toast.error(`Score insuffisant : ${score} % (minimum ${QUIZ_PASS} %). Recommencez.`);
            return;
        }
        await completeModule(m.key, score);
    };

    const retryQuiz = (key) => {
        setAnswers(prev => ({ ...prev, [key]: {} }));
        setSubmittedKey(null);
    };

    const downloadCertificate = async () => {
        if (!session?.access_token) { toast.error('Session expirée'); return; }
        setDownloadingCert(true);
        try {
            const res = await axios.get(`${API}/training/${slug}/certificate.pdf`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url; a.download = `certificat-${slug}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
            toast.success('Certificat téléchargé');
        } catch (e) {
            const detail = e.response?.data ? (typeof e.response.data === 'string' ? e.response.data : JSON.stringify(e.response.data)) : e.message;
            toast.error(`Téléchargement échoué : ${detail}`);
        } finally { setDownloadingCert(false); }
    };

    if (!useCase) return <div className="text-center py-20 text-muted-foreground">{t('common.loading')}</div>;

    const fallbackBody = (key) => {
        const fr = {
            context: '**Cadrage du projet.** Définissez les parties prenantes, le calendrier macro et les objectifs mesurables.',
            aps: '**Avant-projet sommaire.** Premiers chiffrages, esquisses techniques, estimation ±20 %.',
            apd: '**Avant-projet détaillé.** Plans techniques, devis détaillés, estimation ±5 %.',
        };
        return fr[key] || 'Contenu du module en cours de préparation.';
    };
    const moduleBody = (m) => {
        const fromDb = lang === 'fr' ? m?.body_fr : m?.body_en;
        if (fromDb && fromDb.trim().length > 0) return fromDb;
        return fallbackBody(m?.key);
    };

    const moduleAnswers = (active && answers[active.key]) || {};
    const isSubmitted = submittedKey === active?.key;
    const computeScore = () => {
        if (!active?.quiz) return 0;
        let correct = 0;
        active.quiz.forEach((q, i) => { if (moduleAnswers[i] === q.correct) correct++; });
        return Math.round((correct / active.quiz.length) * 100);
    };
    const lastScore = isSubmitted ? computeScore() : null;

    return (
        <div className="space-y-8">
            <div>
                <Link to="/app/formation" className="text-sm text-foreground/60 hover:text-primary inline-flex items-center mb-4">
                    <ArrowLeft size={14} className="mr-1" /> {t('common.back')}
                </Link>
                <div className="grid md:grid-cols-12 gap-6 items-end">
                    <div className="md:col-span-7">
                        <div className="eyebrow mb-2 text-secondary">{useCase.sector}</div>
                        <h1 className="font-heading text-4xl">{lang === 'fr' ? useCase.title_fr : useCase.title_en}</h1>
                        <p className="text-foreground/70 mt-3">{lang === 'fr' ? useCase.summary_fr : useCase.summary_en}</p>
                    </div>
                    <div className="md:col-span-5">
                        <div className="card-soft p-5">
                            <div className="flex items-center justify-between mb-2">
                                <div className="eyebrow">Progression</div>
                                <div className="text-xs flex items-center text-muted-foreground"><Clock size={12} className="mr-1" /> {useCase.duration_minutes} min</div>
                            </div>
                            <div className="h-2 rounded-full bg-surface-muted overflow-hidden">
                                <div className="h-full bg-primary transition-all" style={{ width: `${progress.progress || 0}%` }} />
                            </div>
                            <div className="text-right text-sm font-mono mt-2" data-testid="progress-pct">{progress.progress || 0}%</div>
                            {allDone && (
                                <Button
                                    onClick={downloadCertificate}
                                    disabled={downloadingCert}
                                    className="w-full mt-4 bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                                    data-testid="download-certificate-btn"
                                >
                                    {downloadingCert ? 'Génération…' : <><Award size={16} className="mr-2" /> Télécharger mon certificat</>}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-12 gap-6">
                <aside className="md:col-span-4 card-soft p-5 space-y-1">
                    {modules.map((m, idx) => (
                        <button
                            key={m.key}
                            onClick={() => { setActiveIdx(idx); setSubmittedKey(null); }}
                            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${idx === activeIdx ? 'bg-primary-light text-primary font-medium' : 'hover:bg-surface-muted'}`}
                            data-testid={`module-tab-${m.key}`}
                        >
                            {done.has(m.key) ? <CheckCircle2 size={16} className="text-secondary" /> : <Circle size={16} className="text-muted-foreground" />}
                            <span className="flex-1">{lang === 'fr' ? m.title_fr : m.title_en}</span>
                            {done.has(m.key) && quizScores[m.key] != null && (
                                <span className="text-[10px] font-mono text-muted-foreground">{quizScores[m.key]}%</span>
                            )}
                        </button>
                    ))}
                </aside>

                <article className="md:col-span-8 card-soft p-8">
                    {active && (
                        <>
                            <div className="eyebrow mb-2">{t('formation.module')} {activeIdx + 1} / {modules.length}</div>
                            <h2 className="font-heading text-3xl mb-5">{lang === 'fr' ? active.title_fr : active.title_en}</h2>
                            <div className="prose prose-sm max-w-none text-foreground/85 leading-relaxed prose-headings:font-heading prose-strong:text-ink prose-li:marker:text-primary"
                                dangerouslySetInnerHTML={{ __html: renderRichContent(moduleBody(active)) }} />

                            {/* Quiz */}
                            {Array.isArray(active.quiz) && active.quiz.length > 0 && (
                                <div className="mt-8 pt-8 border-t border-border" data-testid={`quiz-${active.key}`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-heading text-xl">Quiz · validez vos acquis</h3>
                                        <div className="text-xs text-muted-foreground">Score minimum : {QUIZ_PASS} %</div>
                                    </div>
                                    <div className="space-y-5">
                                        {active.quiz.map((q, qi) => {
                                            const picked = moduleAnswers[qi];
                                            return (
                                                <div key={qi} className="space-y-2">
                                                    <div className="text-sm font-medium">{qi + 1}. {q.q}</div>
                                                    <div className="grid sm:grid-cols-2 gap-2">
                                                        {q.options.map((opt, oi) => {
                                                            const selected = picked === oi;
                                                            const isCorrect = isSubmitted && oi === q.correct;
                                                            const isWrong = isSubmitted && selected && oi !== q.correct;
                                                            const cls = isCorrect
                                                                ? 'border-secondary bg-secondary/10 text-secondary'
                                                                : isWrong
                                                                    ? 'border-destructive bg-destructive/10 text-destructive'
                                                                    : selected
                                                                        ? 'border-primary bg-primary-light text-primary'
                                                                        : 'border-border hover:bg-surface-muted';
                                                            return (
                                                                <button
                                                                    key={oi}
                                                                    type="button"
                                                                    disabled={isSubmitted && lastScore >= QUIZ_PASS}
                                                                    onClick={() => setAnswers(prev => ({
                                                                        ...prev,
                                                                        [active.key]: { ...(prev[active.key] || {}), [qi]: oi },
                                                                    }))}
                                                                    className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${cls}`}
                                                                    data-testid={`quiz-${active.key}-q${qi}-o${oi}`}
                                                                >
                                                                    {opt}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-6 flex items-center gap-3 flex-wrap">
                                        {!done.has(active.key) ? (
                                            <Button
                                                onClick={() => submitQuiz(active)}
                                                className="bg-primary hover:bg-primary-hover text-primary-foreground"
                                                data-testid={`quiz-submit-${active.key}`}
                                            >
                                                Valider le quiz
                                            </Button>
                                        ) : (
                                            <div className="text-sm text-secondary flex items-center gap-2">
                                                <CheckCircle2 size={16} /> Module validé · {quizScores[active.key]} %
                                            </div>
                                        )}
                                        {isSubmitted && lastScore != null && lastScore < QUIZ_PASS && (
                                            <>
                                                <div className="text-sm text-destructive flex items-center gap-1.5">
                                                    <XCircle size={14} /> {lastScore} % — Recommencez
                                                </div>
                                                <Button variant="ghost" size="sm" onClick={() => retryQuiz(active.key)} data-testid={`quiz-retry-${active.key}`}>
                                                    Réessayer
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Fallback: no quiz → simple complete button */}
                            {(!Array.isArray(active.quiz) || active.quiz.length === 0) && !done.has(active.key) && (
                                <div className="mt-8">
                                    <Button
                                        onClick={() => completeModule(active.key, 100)}
                                        className="bg-primary hover:bg-primary-hover text-primary-foreground"
                                        data-testid={`complete-module-${active.key}`}
                                    >
                                        Marquer comme terminé
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </article>
            </div>
        </div>
    );
}
